import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Pencil, RotateCcw } from 'lucide-react';

function EditScheduleDialog({ open, onClose, schedule, onConfirm, onReopen }) {
  const [noteText, setNoteText] = useState('');
  const [assignedQuantity, setAssignedQuantity] = useState('');
  const [completedQuantity, setCompletedQuantity] = useState('');
  const [scrappedQuantity, setScrappedQuantity] = useState('');
  const [error, setError] = useState('');

  const isTerminal = schedule && ['completed', 'rolled_over'].includes(schedule.status);

  useEffect(() => {
    if (open) {
      setNoteText(schedule?.notes || '');
      setAssignedQuantity(schedule?.assigned_quantity?.toString() || '');
      setCompletedQuantity(schedule?.completed_quantity?.toString() || '');
      setScrappedQuantity(schedule?.scrapped_quantity?.toString() || '');
      setError('');
    }
  }, [open, schedule]);

  const handleConfirm = () => {
    const qty = parseInt(assignedQuantity, 10);
    if (isNaN(qty) || qty < 1) {
      setError('计划数量必须大于等于 1');
      return;
    }
    const completedQty = parseInt(completedQuantity, 10) || 0;
    const scrappedQty = parseInt(scrappedQuantity, 10) || 0;
    if (completedQty < 0 || scrappedQty < 0) {
      setError('完成/报废数量不能为负数');
      return;
    }
    if (!isTerminal && qty < completedQty) {
      setError(`计划数量不能小于已完成数量 (${completedQty})`);
      return;
    }
    const updates = {
      notes: noteText.trim(),
      assigned_quantity: qty,
    };
    if (isTerminal) {
      updates.completed_quantity = completedQty;
      updates.scrapped_quantity = scrappedQty;
    }
    onConfirm?.(updates);
  };

  const handleReopen = () => {
    onReopen?.(schedule);
  };

  const handleClose = () => {
    setNoteText(schedule?.notes || '');
    setAssignedQuantity(schedule?.assigned_quantity?.toString() || '');
    setCompletedQuantity(schedule?.completed_quantity?.toString() || '');
    setScrappedQuantity(schedule?.scrapped_quantity?.toString() || '');
    setError('');
    onClose?.();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="w-[calc(100%-2rem)] sm:max-w-[420px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-4 w-4" />
            {isTerminal ? '修正排程项' : '编辑排程项'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {schedule && (
            <div className="text-sm text-gray-600 space-y-1">
              <div><span className="font-medium">工序:</span> {schedule.step_name}</div>
              <div><span className="font-medium">产品:</span> {schedule.finished_product_code}</div>
              <div><span className="font-medium">工单:</span> {schedule.production_order_number}-{String(schedule.item_index + 1).padStart(3, '0')}</div>
              <div><span className="font-medium">状态:</span> {schedule.status}</div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="schedule-assigned-quantity">计划数量（件）</Label>
            <Input
              id="schedule-assigned-quantity"
              type="number"
              min={1}
              value={assignedQuantity}
              onChange={(e) => setAssignedQuantity(e.target.value)}
              placeholder="请输入计划数量"
            />
          </div>

          {isTerminal && (
            <>
              <div className="space-y-2">
                <Label htmlFor="schedule-completed-quantity">完成数量（件）</Label>
                <Input
                  id="schedule-completed-quantity"
                  type="number"
                  min={0}
                  value={completedQuantity}
                  onChange={(e) => setCompletedQuantity(e.target.value)}
                  placeholder="请输入完成数量"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="schedule-scrapped-quantity">报废数量（件）</Label>
                <Input
                  id="schedule-scrapped-quantity"
                  type="number"
                  min={0}
                  value={scrappedQuantity}
                  onChange={(e) => setScrappedQuantity(e.target.value)}
                  placeholder="请输入报废数量"
                />
              </div>
            </>
          )}

          <div className="space-y-2">
            <Label htmlFor="schedule-note">备注内容</Label>
            <Textarea
              id="schedule-note"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="请输入备注内容..."
              rows={3}
              className="resize-none"
            />
          </div>

          {error && (
            <div className="text-xs text-red-600">{error}</div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose}>取消</Button>
          {isTerminal && (
            <Button variant="secondary" onClick={handleReopen}>
              <RotateCcw className="h-4 w-4 mr-1" />
              重新打开
            </Button>
          )}
          <Button onClick={handleConfirm}>保存</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default EditScheduleDialog;
